import { ReactElement, useEffect, useMemo, useState } from 'react';
import { ErrorOutline } from '@mui/icons-material';
import { Grid } from '@mui/material';
import DOMPurify from 'dompurify';
import Modal from '~/ui/components/common/Modal';
import NoteHeader from '~/ui/components/reusable/Notes/Header';
import styles from './TriageRequestPopup.module.scss';
import Loader from '~/ui/components/common/Loader';
import { extractErrorMessage } from '~/utils/error';
import { useStoreActions, useStoreState } from '~/store/hooks';
import { isoToFormat } from '~/utils/date';
import Form from './Form';
import { ITriageRequestUpdate } from '~/services/api/triage/types';

interface IProps {
  noteId: number;
  onUpdated: () => void;
  onClose: () => void;
}

const TriageRequestPopup = ({ noteId, onUpdated, onClose }: IProps): ReactElement => {
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);

  const { request } = useStoreState(state => state.triage);

  const { onGetTriageRequestByNoteId, onUpdateTriageRequestByNoteId } = useStoreActions(
    actions => actions.triage,
  );
  const showError = useStoreActions(actions => actions.snackbar.showError);

  const safeNoteContentHtml = useMemo(
    () => (request === null ? '' : DOMPurify.sanitize(request.noteContentHtml)),
    [request],
  );

  const onMount = async () => {
    try {
      await onGetTriageRequestByNoteId(noteId);
      setIsLoading(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const onSubmit = async (values: ITriageRequestUpdate) => {
    try {
      setIsUpdating(true);
      await onUpdateTriageRequestByNoteId({ noteId, values });
      onUpdated();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    onMount();
  }, []);

  return (
    <Modal open title="Triage Notification" className={styles.modal} onClose={onClose}>
      <div className={styles.inner}>
        {isLoading ? (
          <Loader />
        ) : (
          <Grid container flexDirection="column" spacing={2}>
            <Grid item>
              <div className={styles.tab}>{`Program: ${request.programName}`}</div>
            </Grid>
            <Grid item>
              <NoteHeader
                fullName={request.patientFullName}
                subjectId={request.patientSubjectId}
                dateOfBirth={request.patientDateOfBirth}
              />
            </Grid>
            <Grid item>
              <Grid container justifyContent="space-between">
                <Grid item>
                  <b>Issue:</b> {request.issueName}
                </Grid>
                {request.isHighPriority && (
                  <Grid item className={styles.highPriorityIcon}>
                    <Grid container alignItems="center">
                      <Grid item>
                        <ErrorOutline />
                      </Grid>
                      <Grid item>High Priority</Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <b>Note:</b> <span dangerouslySetInnerHTML={{ __html: safeNoteContentHtml }} />
            </Grid>
            <Grid item>
              <Grid container justifyContent="space-between">
                <Grid item>
                  <b>Issue Date:</b>{' '}
                  {isoToFormat(request.noteCreatedAt, 'dd MMM y HH:mm', request.patientTimezone)}
                </Grid>
                <Grid item>
                  <b>Reported By:</b> {request.noteCreatedByUserFullName}
                </Grid>
              </Grid>
            </Grid>
            <Form request={request} isUpdating={isUpdating} onSubmit={onSubmit} />
          </Grid>
        )}
      </div>
    </Modal>
  );
};

export default TriageRequestPopup;
